import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  defaultSort,
  playerColumns,
  type PlayerColumnKey,
  type PlayerRow,
} from '../config/playerStatsTableConfig';
import { recordBadges } from '../config/recordBadges';
import { apiFetch } from '../lib/api';

type SortDirection = 'asc' | 'desc';

type PlayerStatsResponse = {
  items: PlayerRow[];
};

type PlayerStatsTableProps = {
  onSelectPlayer?: (nickname: string) => void;
  title?: string;
  subtitle?: string;
  apiPath?: string;
  queryKey?: string[];
  hiddenColumns?: PlayerColumnKey[];
  helpContext?: 'default' | 'artillery';
};

export function PlayerStatsTable({
  onSelectPlayer,
  title = 'Статистика игроков',
  subtitle = 'Аналитика индивидуальных показателей',
  apiPath = '/player-stats/',
  queryKey = ['player-stats'],
  hiddenColumns = [],
  helpContext = 'default',
}: PlayerStatsTableProps): JSX.Element {
  const hasMedic = !hiddenColumns.includes('medic');
  const isArtillery = helpContext === 'artillery';
  const infoContent: Partial<Record<PlayerColumnKey, { title: string; description: ReactNode }>> = {
    medic: {
      title: 'Медицина',
      description: (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            Σ MP
          </div>
          <div>
            <strong>MP</strong> — Очки за мед. действия:
            <br />
            <strong>-Бандаж</strong> — 0.009;
            <br />
            <strong>-Апт-ка (хир. набор)</strong> — 0.17;
            <br />
            <strong>-СЛР</strong> — 0.1;
          </div>
          <p>Каждое ваше действие приносит вам очки. Параметр считается как сумма этих очков.</p>
        </div>
      ),
    },
    ego: {
      title: 'ЧСВ',
      description: (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">F / A</div>
          <div>
            <strong>F</strong> — Фраги игрока;
            <br />
            <strong>A</strong> — Посещаемость игрока;
          </div>
        </div>
      ),
    },
    efficiency: {
      title: 'КПД',
      description: isArtillery ? (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            S - (T / F) - (T / A)
          </div>
          <div>
            <strong>C</strong> — ЧСВ;
            <br />
            <strong>T</strong> — ТК игрока;
            <br />
            <strong>F</strong> — Фраги игрока;
            <br />
            <strong>A</strong> — Посещаемость;
          </div>
          <p>
            <strong>КПД = 0</strong> если вы посетили менее 40% игр СГ на ротации.
          </p>
        </div>
      ) : hasMedic ? (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            C + (M / A) + 0.009 · S - (T / F) - (T / A)
          </div>
          <div>
            <strong>C</strong> — ЧСВ;
            <br />
            <strong>M</strong> — Медицина;
            <br />
            <strong>S</strong> — Выживаемость;
            <br />
            <strong>T</strong> — ТК игрока;
            <br />
            <strong>F</strong> — Фраги игрока;
            <br />
            <strong>A</strong> — Посещаемость;
          </div>
          <p>
            <strong>КПД = 0</strong> если вы посетили менее 40% игр СГ на ротации.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
            C + 0.009 · S - (T / F) - (T / A)
          </div>
          <div>
            <strong>C</strong> — ЧСВ;
            <br />
            <strong>S</strong> — Выживаемость;
            <br />
            <strong>T</strong> — ТК игрока;
            <br />
            <strong>F</strong> — Фраги игрока;
            <br />
            <strong>A</strong> — Посещаемость;
          </div>
          <p>
            <strong>КПД = 0</strong> если вы посетили менее 40% игр СГ на ротации.
          </p>
        </div>
      ),
    },
  };

  const { data: playerStatsResponse, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiFetch<PlayerStatsResponse>(apiPath),
  });
  const [sortKey, setSortKey] = useState<PlayerColumnKey>(defaultSort.key);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort.direction);
  const [activeInfoKey, setActiveInfoKey] = useState<PlayerColumnKey | null>(null);
  const [filters, setFilters] = useState<Record<PlayerColumnKey, string>>(() => {
    const initial = {} as Record<PlayerColumnKey, string>;
    playerColumns.forEach((col) => {
      initial[col.key] = '';
    });
    return initial;
  });

  const visibleColumns = useMemo(
    () => playerColumns.filter((column) => !hiddenColumns.includes(column.key)),
    [hiddenColumns]
  );

  const handleSort = (key: PlayerColumnKey) => {
    setSortKey(key);
    setSortDirection((prev) => (key === sortKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
  };

  const handleFilterChange = (key: PlayerColumnKey, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const rows = playerStatsResponse?.items ?? [];

  const efficiencyRange = useMemo(() => {
    const values = rows.map((row) => row.efficiency);
    if (values.length === 0) {
      return { min: 0, max: 0 };
    }
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [rows]);

  const kpiStyle = (value: number) => {
    const spread = Math.max(efficiencyRange.max - efficiencyRange.min, 0.001);
    const norm = Math.max(0, Math.min(1, (value - efficiencyRange.min) / spread));
    const startAlpha = norm * 0.25;
    const endAlpha = norm * 0.7;

    return {
      background: `linear-gradient(90deg, rgba(255, 222, 0, ${startAlpha.toFixed(3)}) 0%, rgba(255, 222, 0, ${endAlpha.toFixed(3)}) 100%)`,
      color: '#0f1115',
    } as const;
  };

  const filteredAndSorted = useMemo(() => {
    const effectiveSortKey = visibleColumns.some((column) => column.key === sortKey)
      ? sortKey
      : defaultSort.key;
    const filtered = rows.filter((row) =>
      visibleColumns.every((col) => {
        const query = filters[col.key]?.trim();
        if (!query) return true;
        const value = row[col.key];
        return value
          .toString()
          .toLowerCase()
          .includes(query.toLowerCase());
      })
    );

    const sorted = [...filtered].sort((a, b) => {
      const colMeta = visibleColumns.find((col) => col.key === effectiveSortKey);
      if (!colMeta) return 0;
      const aVal = a[effectiveSortKey];
      const bVal = b[effectiveSortKey];

      if (colMeta.type === 'number') {
        const diff = Number(aVal) - Number(bVal);
        return sortDirection === 'asc' ? diff : -diff;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr === bStr) return 0;
      const result = aStr > bStr ? 1 : -1;
      return sortDirection === 'asc' ? result : -result;
    });

    return sorted;
  }, [filters, rows, sortDirection, sortKey, visibleColumns]);

  const renderSortIndicator = (key: PlayerColumnKey) => {
    if (sortKey !== key) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const championBackground = 'rgb(255, 193, 7)';

  useEffect(() => {
    if (!activeInfoKey) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-info-key]')) return;
      setActiveInfoKey(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeInfoKey]);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">{subtitle}</p>
        <h2 className="text-3xl font-bold">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="card p-6 text-center text-sm text-muted-foreground">
          {isLoading ? 'Загрузка статистики…' : 'Нет данных.'}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="px-3 py-3 text-center align-middle font-semibold">
                      <div className="mx-auto flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="flex items-center justify-center gap-2 rounded-md px-2 py-1 text-foreground transition hover:bg-muted/70"
                        >
                          <span>{col.label}</span>
                          <span className="text-muted-foreground">{renderSortIndicator(col.key)}</span>
                        </button>
                        {infoContent[col.key] ? (
                          <div className="relative" data-info-key={col.key}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActiveInfoKey((prev) => (prev === col.key ? null : col.key));
                              }}
                              className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-muted-foreground transition hover:text-foreground"
                              aria-label={`Справка: ${col.label}`}
                              aria-expanded={activeInfoKey === col.key}
                            >
                              ?
                            </button>
                            {activeInfoKey === col.key ? (
                              <div className="absolute right-0 top-7 z-20 w-64 rounded-md border border-border bg-card p-3 text-left text-xs text-foreground shadow-lg">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-foreground">
                                    {infoContent[col.key]?.title}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setActiveInfoKey(null);
                                    }}
                                    className="flex h-6 w-6 items-center justify-center rounded-full text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                    aria-label="Закрыть подсказку"
                                  >
                                    ✕
                                  </button>
                                </div>
                                <p className="mt-1 text-muted-foreground">
                                  {infoContent[col.key]?.description}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={`${col.key}-filter`} className="px-3 pb-3 text-center align-middle">
                      <input
                        type="text"
                        value={filters[col.key]}
                        onChange={(e) => handleFilterChange(col.key, e.target.value)}
                        placeholder="Поиск"
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-center text-xs text-foreground focus:border-primary focus:outline-none"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map((row, idx) => {
                  const badge = row.recordType ? recordBadges[row.recordType] : undefined;
                  return (
                    <motion.tr
                      key={row.callsign}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18, delay: idx * 0.02 }}
                      className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                    >
                      {visibleColumns.map((column) => {
                        switch (column.key) {
                          case 'rank':
                            return (
                              <td
                                key={column.key}
                                className="px-3 py-3 text-center align-middle font-semibold text-foreground dark:text-primary"
                              >
                                <div className="flex items-center justify-center gap-2">
                                  <span>{row.rank}</span>
                                  {row.delta ? (
                                    <span
                                      className={`text-xs font-semibold ${
                                        row.delta > 0 ? 'text-emerald-600' : 'text-red-600'
                                      }`}
                                    >
                                      {row.delta > 0 ? '↑' : '↓'} {Math.abs(row.delta)}
                                    </span>
                                  ) : null}
                                </div>
                              </td>
                            );
                          case 'callsign':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle font-semibold">
                                <a
                                  href={`/player/${encodeURIComponent(row.callsign)}`}
                                  className="inline-flex items-stretch justify-center rounded hover:underline"
                                  aria-label={`Открыть статистику игрока ${row.callsign}`}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    onSelectPlayer?.(row.callsign);
                                  }}
                                >
                                  {badge ? (
                                    <>
                                      <span
                                        className="px-2 py-1"
                                        style={{ backgroundColor: championBackground, color: '#0f1115' }}
                                      >
                                        {row.callsign}
                                      </span>
                                      <span className="flex items-center justify-center px-2 py-1 bg-background dark:bg-card">
                                        <img
                                          src={badge.icon}
                                          alt={badge.title}
                                          title={badge.title}
                                          className="h-6 w-6"
                                        />
                                      </span>
                                    </>
                                  ) : (
                                    <span className="px-2 py-1">{row.callsign}</span>
                                  )}
                                </a>
                              </td>
                            );
                          case 'attendance':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.attendance}
                              </td>
                            );
                          case 'frags':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.frags}
                              </td>
                            );
                          case 'tk':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.tk}
                              </td>
                            );
                          case 'survival':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.survival}
                              </td>
                            );
                          case 'medic':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.medic.toFixed(3)}
                              </td>
                            );
                          case 'ego':
                            return (
                              <td key={column.key} className="px-3 py-3 text-center align-middle">
                                {row.ego.toFixed(3)}
                              </td>
                            );
                          case 'efficiency':
                            return (
                              <td
                                key={column.key}
                                className="kpi-highlight px-3 py-3 text-center align-middle font-semibold rounded"
                                style={kpiStyle(row.efficiency)}
                              >
                                {row.efficiency.toFixed(3)}
                              </td>
                            );
                          default:
                            return null;
                        }
                      })}
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
            По умолчанию отсортировано по КПД по убыванию. Используйте стрелки в заголовках и поисковые поля для фильтрации.
          </div>
        </div>
      )}
    </div>
  );
}
